import React, { useState, useEffect, useCallback } from 'react';
import { notificationsAPI } from '../services/api';
import { dateFr } from '../services/dates';

/**
 * SUIVI DE STAGE — l'onglet NOTIFICATIONS (lot N.1)
 * ==================================================
 * ⚠️ Onglet PRINCIPAL, après « Annuaire entreprises » (Bertrand, recette N.1) : le récapitulatif couvre les
 * Stagiaires, l'Alternance ET la Certification — rangé dans Stagiaires → Paramétrage, il semblait ne
 * concerner que les stagiaires.
 * Plan : doc/SUIVI_DEV.md (lot N.1, CN1 → CN8)
 *
 * « Mes notifications » : le réglage du COMPTE CONNECTÉ, pour CE département (CN1). Un curseur, les familles
 * voulues, et « M'envoyer le récapitulatif maintenant » pour voir à quoi il ressemble. Le récapitulatif part
 * chaque jour ouvré à l'heure choisie (E180, v93 : menu dans la phrase, 7 h à 18 h, 8 h par défaut), et seulement
 * s'il y a quelque chose à dire.
 *
 * ⚠️ Pas de `modifiable` ici : un compte en CONSULTATION règle quand même SES notifications (la route
 * /api/notifications le permet, et ne touche qu'à son propre compte).
 */
const FAMILLES = [
    { cle: 'stagiaires', libelle: 'Stagiaires', aide: "fiches saisies et validées, documents déposés, grilles reçues ; décisions, documents à vérifier, relances dues" },
    { cle: 'alternance', libelle: 'Alternance', aide: "alternants dont le registre est à compléter" },
    { cle: 'certification', libelle: 'Certification', aide: "dépôts validés, corrections, décisions du maître, envois échoués ; sans certificateur, liens à envoyer" }
];

const RESULTATS = { envoye: 'envoyé', rien: 'rien à signaler — aucun courriel', echec: 'échec', en_cours: 'en cours' };

function StageNotifications({ departementId }) {
    const [r, setR] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [message, setMessage] = useState(null);
    const [enCours, setEnCours] = useState(false);

    const lire = useCallback(async () => {
        try { setR((await notificationsAPI.lire(departementId)).data); }
        catch (e) { setErreur(e.response?.data?.error || 'Lecture du réglage impossible'); }
    }, [departementId]);
    useEffect(() => { lire(); }, [lire]);

    const ecrire = async (abonnement) => {
        setEnCours(true); setErreur(null); setMessage(null);
        try {
            setR((await notificationsAPI.ecrire(departementId, abonnement)).data);
            setMessage({ ton: 'ok', texte: abonnement.actif ? `Enregistré : vous recevrez le récapitulatif chaque jour ouvré à ${abonnement.heure ?? 8} h.` : 'Enregistré : le récapitulatif est désactivé.' });
        } catch (e) { setErreur(e.response?.data?.error || "L'enregistrement a échoué"); }
        finally { setEnCours(false); }
    };

    const maintenant = async () => {
        setEnCours(true); setErreur(null); setMessage(null);
        try {
            const x = (await notificationsAPI.maintenant(departementId)).data;
            setMessage(x.ok
                ? { ton: 'ok', texte: `Récapitulatif envoyé à ${x.a}${x.redirige_vers ? ` (serveur de test : redirigé vers ${x.redirige_vers})` : ''} — ${x.evenements} événement${x.evenements > 1 ? 's' : ''}, ${x.a_faire} point${x.a_faire > 1 ? 's' : ''} à faire. Il ne compte pas comme celui du jour.` }
                : { ton: 'ko', texte: `L'envoi a échoué : ${x.erreur}` });
        } catch (e) { setErreur(e.response?.data?.error || "L'envoi a échoué"); }
        finally { setEnCours(false); }
    };

    if (erreur && !r) return <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>;
    if (!r) return <div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Lecture…</div></div>;

    const a = r.abonnement;
    const basculerFamille = (cle) => {
        const familles = a.familles.includes(cle) ? a.familles.filter((f) => f !== cle) : [...a.familles, cle];
        if (!familles.length) { setErreur('Gardez au moins une famille.'); return; }
        ecrire({ ...a, familles });
    };
    const role = r.role.tout ? null : r.role.responsable ? 'responsable des stages' : 'assistante';

    return (
        <div className="param-card notif-carte">
            <div className="param-card-header">
                <span><i className="fa-solid fa-bell" /> Mes notifications — compte de <b>{r.compte.nom}</b></span>
                <span className="notif-adresse">{r.compte.email || 'aucune adresse sur ce compte'}</span>
            </div>
            <div className="param-card-body">
                {!r.boite_ok && (
                    <div className="stg-encart stg-encart-alerte">
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span>La boîte d'envoi du département n'est pas configurée : <b>aucun récapitulatif ne peut partir</b>. Administration → Départements → Envoi des courriels.</span>
                    </div>
                )}

                <div className="notif-ligne">
                    <button type="button" className={'toggle' + (a.actif ? ' on' : '')} disabled={enCours} aria-pressed={a.actif}
                            aria-label="Recevoir le récapitulatif quotidien" onClick={() => ecrire({ ...a, actif: !a.actif })} />
                    <div>
                        <div className="notif-titre">Recevoir le récapitulatif quotidien</div>
                        <div className="notif-aide">
                            Chaque jour ouvré à{' '}
                            <select className="notif-heure" value={a.heure ?? 8} disabled={enCours} aria-label="Heure d'envoi du récapitulatif"
                                    onChange={(e) => ecrire({ ...a, heure: Number(e.target.value) })}>
                                {Array.from({ length: (r.heures?.max ?? 18) - (r.heures?.min ?? 7) + 1 }, (_, i) => (r.heures?.min ?? 7) + i)
                                    .map((h) => <option key={h} value={h}>{h} h</option>)}
                            </select>, un courriel : ce que les étudiants et les maîtres ont fait depuis le précédent,
                            et ce qu'il vous reste à faire{role ? <> en tant que <b>{role}</b> (Paramétrage général → Équipe)</> : null}. <b>Rien n'est envoyé</b> les jours sans rien à signaler.
                        </div>
                    </div>
                </div>

                <div className="notif-familles">
                    {FAMILLES.map((f) => (
                        <label key={f.cle} className="notif-famille">
                            <input type="checkbox" checked={a.familles.includes(f.cle)} disabled={enCours} onChange={() => basculerFamille(f.cle)} />
                            <span><b>{f.libelle}</b> — <span className="notif-aide">{f.aide}</span></span>
                        </label>
                    ))}
                </div>

                <div className="notif-pied">
                    <button type="button" className="btn btn-outline btn-sm" disabled={enCours || !r.boite_ok || !r.compte.email} onClick={maintenant}
                            title="Les dernières 24 h, même s'il n'y a rien : pour voir à quoi il ressemble">
                        <i className={'fa-solid ' + (enCours ? 'fa-circle-notch stg-tourne' : 'fa-paper-plane')} /> M'envoyer le récapitulatif maintenant
                    </button>
                    {r.dernier && (
                        <span className="notif-aide">
                            Dernier récapitulatif quotidien : {dateFr(r.dernier.jour)} — {RESULTATS[r.dernier.resultat] || r.dernier.resultat}
                            {r.dernier.erreur ? ` (${r.dernier.erreur})` : ''}
                        </span>
                    )}
                </div>

                {message && <div className={'stg-encart ' + (message.ton === 'ok' ? 'stg-encart-ok' : 'stg-encart-erreur')}><i className={'fa-solid ' + (message.ton === 'ok' ? 'fa-circle-check' : 'fa-circle-exclamation')} /><span>{message.texte}</span></div>}
                {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            </div>
        </div>
    );
}

export default StageNotifications;
